// LocalStorage keys (single source of truth for auth persistence)
export const STORAGE_KEYS = {
  AUTH: 'isAuthenticated',
  USER: 'user',
  ACCESS_TOKEN: 'petpulse_access_token',
  REFRESH_TOKEN: 'petpulse_refresh_token',
  ID_TOKEN: 'petpulse_id_token',
  PROFESSIONAL_ID: 'petpulse_professional_id',
  ORGANIZATION_IDS: 'petpulse_organization_ids',
  ORGANIZATION_TYPE: 'petpulse_organization_type',
  ORGANIZATION_CITY: 'petpulse_organization_city',
  ORGANIZATION_ACTIVE_ID: 'petpulse_active_organization_id',
  ORGANIZATION_ACTIVE_NAME: 'petpulse_active_organization_name',
  ORGANIZATION_ACTIVE_LOGO: 'petpulse_active_organization_logo',
};

// External URLs (avoid magic strings)
export const EXTERNAL_URLS = {
  HELP_CENTER: 'https://help.pawdel.com/knowledge-base',
  VACCINATOR_ICON: 'https://cdn.shopify.com/s/files/1/0817/7844/2548/files/Pet_Pulse_Icon_-_Vaccinator_Page.svg?v=1770881477',
};

// Organization type values
export const ORGANIZATION_TYPES = {
  GOVERNMENT: 'government',
  PRIVATE: 'private',
};

// Cities for city filter dropdown (government/org-scoped data). One entry per city (e.g. Parañaque only; backend matches both spellings).
export const CITIES = [
  // Metro Manila
  'Caloocan',
  'Las Piñas',
  'Makati',
  'Malabon',
  'Mandaluyong',
  'Manila',
  'Marikina',
  'Muntinlupa',
  'Navotas',
  'Parañaque',
  'Pasay',
  'Pasig',
  'Quezon City',
  'San Juan',
  'Taguig',
  'Valenzuela',
  // Cavite
  'Bacoor',
  'Imus',
  // Bulacan
  'Meycauayan City',
  'Malolos City',
  'San Jose del Monte City',
  // Rizal / Calabarzon
  'Antipolo',
  'Calamba City',
  'Lipa City',
  // Visayas / Mindanao
  'Cebu City',
  'Davao City',
];

// API Endpoints
export const API_ENDPOINTS = {
  PETS: '/api/pets',
  CLINICAL_INTELLIGENCE: '/api/clinical-intelligence',
  SEARCH: '/api/search',
  VACCINATION_DIAGNOSTICS: '/api/vaccination-diagnostics',
  REMINDERS: '/api/reminders',
  HEALTH: '/health'
};

// Navigation Routes
export const ROUTES = {
  DASHBOARD: '/',
  SEARCH: '/search',
  CLINICAL_INTELLIGENCE: '/clinical-intelligence',
  COMPREHENSIVE_ANALYTICS: '/comprehensive-analytics'
};

// Chart Colors
export const CHART_COLORS = {
  PRIMARY: '#1E90FF',
  SUCCESS: '#2ECC71',
  WARNING: '#FF8A34',
  DANGER: '#FF6B9E',
  INFO: '#00B7A8',
  SECONDARY: '#FFD166'
};

// Status Colors
export const STATUS_COLORS = {
  // Government Registration Status
  REGISTRATION: {
    TRUE: '#7BD754',  // Green for registered
    FALSE: '#F5423E'  // Red for unregistered
  },
  // Spay/Neuter Status
  SPAY_NEUTER: {
    TRUE: '#7BD754',  // Green for spayed/neutered
    FALSE: '#F5423E'  // Red for not spayed/neutered
  },
  // Species Colors
  SPECIES: {
    DOG: '#41AFD1',   // Blue for dogs
    CAT: '#F8981E'    // Orange for cats
  },
  // Sex Colors
  SEX: {
    MALE: '#D9E650',   // Yellow-green for male
    FEMALE: '#FD6F6C'  // Pink for female
  }
};

// Radial Chart Colors with Opacity
export const RADIAL_CHART_COLORS = {
  // Government Registration Status
  REGISTRATION: {
    TRUE: '#7BD754',  // Green for registered
    FALSE: 'rgba(245, 66, 62, 0.16)'  // Red with 16% opacity for unregistered
  },
  // Spay/Neuter Status
  SPAY_NEUTER: {
    TRUE: '#7BD754',  // Green for spayed/neutered
    FALSE: 'rgba(245, 66, 62, 0.16)'  // Red with 16% opacity for not spayed/neutered
  }
};

// App Configuration
export const APP_CONFIG = {
  NAME: 'Pet Pulse Dashboard',
  VERSION: '1.0.0',
  DESCRIPTION: 'PAWNEC Pet Dashboard - Full Stack Application'
};

// City human populations — 2020 Philippine Census (PSA).
// Estimated pet population = 0.25 × city population (WHO/FAO dog-to-human ratio).
// Keys must match the CITIES list and the orgCity value stored at login.
export const CITY_POPULATIONS = {
  'Caloocan':               1661584,
  'Las Piñas':               606293,
  'Makati':                  582602,
  'Malabon':                 365525,
  'Mandaluyong':             425758,
  'Manila':                 1846513,
  'Marikina':                450741,
  'Muntinlupa':              543445,
  'Navotas':                 249131,
  'Parañaque':               689992,
  'Pasay':                   440656,
  'Pasig':                   803159,
  'Quezon City':            2960048,
  'San Juan':                122180,
  'Taguig':                  886722,
  'Valenzuela':              714978,
  'Bacoor':                  664625,
  'Imus':                    497940,
  'Meycauayan City':         363681,
  'Malolos City':            252074,
  'San Jose del Monte City': 651813,
  'Antipolo':                887399,
  'Calamba City':            454718,
  'Lipa City':               372931,
  'Cebu City':               964169,
  'Davao City':             1776949,
};

// Cache TTLs (milliseconds – frontend)
export const BARANGAY_CACHE_TTL_MS = 5 * 60 * 1000;  // 5 minutes client-side barangay cache
export const DASHBOARD_REFRESH_MS  = 5 * 60 * 1000;  // 5 minutes dashboard auto-refresh

// API pagination / result limits
export const DEFAULT_PAGE_SIZE    = 20;
export const MAX_BARANGAY_RESULTS = 50;
// Reduced from 500 → 100 (Round 2 perf fix).
// Dashboard only requests page 1 of the heatmap; each page is 100 records.
// GeographicAnalytics uses its own larger limit with explicit pagination UI.
export const MAX_HEATMAP_RESULTS  = 100;
export const MAX_MAP_MARKERS      = 50;
